// `unzipper` uses Node.js native `zlib` module.
// This means that most likely it's more performant than an equivalent javascript-only implementation.

// `unzipper` has a bug — it is written in CommonJS and uses a conditional `require()` call
// to create an AWS S3 client instance: `require('@aws-sdk/client-s3')`.
// https://github.com/ZJONSSON/node-unzipper/issues/330
// The author of the package refuses to acknowledge the issue and apply a simple fix.
// The package, therefore, looks like an "abandonware".
// Someone could easily fix the issue though by forking the package.
//
// When not forking to apply the fix, a workaround could be to install "@aws-sdk/client-s3"
// package manually, which introduces unnecessary complexity and "esoteric knowledge" to the application setup.
//
// Another workaround could be to "alias" "@aws-sdk/client-s3" package in a "bundler" configuration file
// with a path to a `*.js` file containing just "export default null". But that kind of a workaround would also
// result in errors when using other packages that happen to `import` anything from "@aws-sdk/client-s3" package,
// so it's not really a workaround but more of a ticking bomb. Not to mention that it introduces
// unnecessary complexity and "esoteric knowledge" to the application setup.
//
import { Parse } from 'unzipper-esm'

import { Buffer } from 'buffer'

/**
 * Reads `*.zip` file contents.
 * @param  {Stream} stream
 * @return {Promise<Record<string,Buffer>>} Resolves to an object holding `*.zip` file entries. P.S. `Buffer` is a `Uint8Array`.
 */
export default function unzipFromStream(stream, { filter } = {}) {
	// The `files` object stores the files and their contents.
	const files = {}

	return new Promise((resolve, reject) => {
		const promises = []

		let errored = false

		const onError = (error) => {
			if (!errored) {
				errored = true
				reject(error)
			}
		}

		stream
			// This first "error" listener catches the original stream errors.
			//
			// That's because the .pipe() method does not automatically propagate errors
			// from a source (input) stream to the destination stream or the end of the pipeline.
			// You would need to attach an 'error' event handler to each stream in the chain.
			//
			// A more convenient alternative would be to use `stream.pipeline()` function:
			// `pipeline(stream1, stream2, (error) => { ... })`
			//
			.on('error', onError)
			// Pipe the input stream through the unzipper stream.
			.pipe(Parse())
			// This second "error" listener catches the unzipper stream errors.
			//
			// That's because the .pipe() method does not automatically propagate errors
			// from a source (input) stream to the destination stream or the end of the pipeline.
			// You would need to attach an 'error' event handler to each stream in the chain.
			//
			// A more convenient alternative would be to use `stream.pipeline()` function:
			// `pipeline(stream1, stream2, (error) => { ... })`
			//
			.on('error', onError)
			// The unzipper stream is closed when all `entries` have been reported.
			.on('finish', () => {
				if (!errored) {
					// Wait for all `entries` to be read.
					// The second argument of `.then()` function is not required
					// but I didn't remove it just to potentially prevent any potential silly bugs
					// in case of some potential changes in some potential future.
					Promise.all(promises).then(() => {
						resolve(files)
					}, onError)
				}
			})
			.on('entry', (entry) => {
				// See if this file should be ignored.
				let ignore = false
				// `entry.type` could be 'Directory' or 'File'.
				// Ignore anything except files.
				if (entry.type === 'Directory') {
					ignore = true
				}
				if (errored) {
					ignore = true
				}
				if (filter) {
					if (!filter({ path: entry.path })) {
						ignore = true
					}
				}

				// If this file should be ignored.
				if (ignore) {
					// Call `entry.autodrain()` when you do not intend to process a specific `entry`'s raw data.
					// Otherwise, if an `entry` is not consumed (via .pipe(), .buffer(), or .autodrain()),
					// the stream will halt, preventing further file processing.
					entry.autodrain().on('error', onError)
					return
				}

				const chunks = []

				promises.push(new Promise((resolve) => {
					// `entry` seems to be a generic Node.js stream.
					// `entry.pipe()` pipes the file contents to a stream.
					// `entry.stream()` returns a readable stream.
					// `entry.buffer()` returns a promise that resolves to a `Buffer` with the file contents.
					entry
						.on('data', (data) => {
							chunks.push(data)
						})
						.on('error', (error) => {
							onError(error)
						})
						.on('finish', () => {
							files[entry.path] = Buffer.concat(chunks)
							resolve()
						})
				}))
			})
	})
}